<script setup lang="ts">
    import type { CameraPlacementId } from '@/views/3d-planner/scene/cameraPlacement';

    const props = defineProps<{
        activePreset: CameraPlacementId | null;
    }>();

    const emit = defineEmits<{
        (e: 'select', id: CameraPlacementId): void;
    }>();

    // Order matches Figma: Default | Coronal | Lateral | Bottom | Top
    const presets: { id: CameraPlacementId; label: string }[] = [
        { id: 'default', label: 'Default' },
        { id: 'coronal', label: 'Coronal' },
        { id: 'lateral', label: 'Lateral' },
        { id: 'bottom', label: 'Bottom' },
        { id: 'axial', label: 'Top' },
    ];

    // Blur the button after selecting so the lingering :focus-visible
    // outline doesn't read as a (ghostly white) "active" state once the
    // user nudges the camera away.
    function onSelect(event: MouseEvent, id: CameraPlacementId) {
        emit('select', id);
        const target = event.currentTarget;
        if (target instanceof HTMLElement) target.blur();
    }
</script>

<template>
    <div class="snap-buttons">
        <v-btn
            v-for="preset in presets"
            :key="preset.id"
            :variant="props.activePreset === preset.id ? 'flat' : 'outlined'"
            :color="props.activePreset === preset.id ? 'primary' : 'white'"
            :class="{ active: props.activePreset === preset.id }"
            class="snap-btn"
            rounded="pill"
            height="36"
            @click="onSelect($event, preset.id)"
        >
            {{ preset.label }}
        </v-btn>
    </div>
</template>

<style scoped lang="scss">
    .snap-buttons {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    // Overrides on top of Vuetify v-btn defaults. Not routed through AppButton
    // — AppButton hard-codes on-light colours.
    .snap-btn {
        font-weight: 400 !important;
        letter-spacing: normal !important;
        padding: 0 16px !important;
        text-transform: none !important;

        &.active {
            font-weight: 700 !important;
        }
    }
</style>
